import { useMemo } from 'react';

import {
	THIRD_PARTY_PROMPT_SOURCE_CONTENT_TYPE,
	type SuggestedPromptTile,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInputParam,
	type AssistanceServiceInjectedParams,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import type { UsePromptSuggestionsReturn } from './types';
import { usePromptSuggestionsState } from './usePromptSuggestionsState';

/** Stream suggestions using the decision supplied by the optional connector controller. */
export const usePromptSuggestionsWithDecision: (
	props: AssistanceServiceInputParam & {
		thirdPartyPrompt?: { enable3pPrompt: boolean; ready: boolean };
		includeThirdParty?: boolean;
	},
) => UsePromptSuggestionsReturn = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		thirdPartyPrompt = { enable3pPrompt: false, ready: true },
		includeThirdParty = false,
	}: AssistanceServiceInjectedParams & {
		thirdPartyPrompt?: { enable3pPrompt: boolean; ready: boolean };
		includeThirdParty?: boolean;
	}) => {
		const suggestions = usePromptSuggestionsState({
			assistanceServiceInjectedParams,
			thirdPartyPrompt,
		});
		// A previous entitled surface may have warmed the shared store with a 3P tile.
		const { data, primaryPrompts, secondaryPrompts } = suggestions;
		const ordinaryPrompts = useMemo(() => {
			if (includeThirdParty) return { data, primaryPrompts, secondaryPrompts };
			const withoutThirdParty = (prompts: SuggestedPromptTile[] | null) =>
				prompts?.filter(
					(prompt) => prompt.source_content_type !== THIRD_PARTY_PROMPT_SOURCE_CONTENT_TYPE,
				) ?? null;
			return {
				data: withoutThirdParty(data),
				primaryPrompts: withoutThirdParty(primaryPrompts),
				secondaryPrompts: withoutThirdParty(secondaryPrompts),
			};
		}, [data, primaryPrompts, secondaryPrompts, includeThirdParty]);
		return { ...suggestions, ...ordinaryPrompts };
	},
);
